// Saisie d'un bon de commande client : en-tête, quantités et prix par type ; historique avant l'ERP (administrateur).
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Collapse, Grid, IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { AddOutlined, AttachFileOutlined, CloseOutlined, DeleteOutline, ExpandMore, UploadFileOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Panel } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { clientAPI, marcheAPI, stockAPI } from '../../services/api';
import { apiError, fcfa } from '../../utils/format';
import { tokens } from '../../theme';
import { typeLabel } from './common';

const digits = (v) => String(v ?? '').replace(/[^\d]/g, '');

const MarcheForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [types, setTypes] = useState([]);
  const [f, setF] = useState({ client_id: '', numero: '', rang: '', date_bc: '', objet: 'Fourniture de poteaux béton armé', delai_paiement_jours: '30', statut: 'OUVERT', notes: '', reprise_paye_ht: '' });
  const [lignes, setLignes] = useState([]);
  const [historique, setHistorique] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('saisie'); // saisie | depot (BC scanné seul, lignes à compléter)
  const [docs, setDocs] = useState([]); // BC scanné(s) joint(s) à l'enregistrement : { file, url }
  const [vu, setVu] = useState(0);
  const [survol, setSurvol] = useState(false);
  const fileInput = useRef(null);
  useEffect(() => () => docs.forEach((d) => URL.revokeObjectURL(d.url)), []); // eslint-disable-line

  const ajouterDocs = (files) => {
    const ok = [];
    for (const file of Array.from(files || [])) {
      if (!/^(application\/pdf|image\/)/.test(file.type)) { setError(`« ${file.name} » : joignez un PDF ou une photo du BC`); continue; }
      if (file.size > 10 * 1024 * 1024) { setError(`« ${file.name} » dépasse 10 Mo`); continue; }
      ok.push({ file, url: URL.createObjectURL(file) });
    }
    if (ok.length) { setDocs((d) => [...d, ...ok].slice(0, 10)); setVu(docs.length); }
  };
  const retirerDoc = (k) => { URL.revokeObjectURL(docs[k].url); setDocs((d) => d.filter((_, j) => j !== k)); setVu(0); };

  useEffect(() => {
    Promise.all([clientAPI.list(), stockAPI.getPbaTypes()]).then(async ([c, t]) => {
      setClients(c.data); setTypes(t.data);
      if (id) {
        const { data: m } = await marcheAPI.get(id);
        setF({ client_id: m.client_id, numero: m.numero, rang: m.rang || '', date_bc: m.date_bc, objet: m.objet || '', delai_paiement_jours: String(m.delai_paiement_jours),
          statut: m.statut, notes: m.notes || '', reprise_paye_ht: String(m.reprise_paye_ht || '') });
        setLignes(m.lignes.length ? m.lignes.map((l) => ({ pba_type: l.pba_type, quantite: String(l.quantite), prix_unitaire_ht: String(l.prix_unitaire_ht),
          reprise_livree: String(l.reprise_livree), reprise_facturee: String(l.reprise_facturee) })) : [{ pba_type: '', quantite: '', prix_unitaire_ht: '', reprise_livree: '', reprise_facturee: '' }]);
        setHistorique(m.lignes.some((l) => l.reprise_livree || l.reprise_facturee) || m.reprise_paye_ht > 0);
      } else {
        const senelec = c.data.find((x) => /senelec/i.test(x.nom));
        setF((x) => ({ ...x, client_id: senelec?.id || '', date_bc: new Date().toISOString().slice(0, 10) }));
        setLignes([{ pba_type: '', quantite: '', prix_unitaire_ht: '', reprise_livree: '', reprise_facturee: '' }]);
      }
    }).catch((e) => setError(apiError(e)));
  }, [id]);

  const setL = (i, patch) => setLignes((p) => p.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const total = lignes.reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0), 0);
  const senelec = /senelec/i.test(clients.find((c) => c.id === f.client_id)?.nom || '');
  const histo = historique && senelec;
  const c = docs.length > 0; // aperçu du BC affiché à côté : formulaire plus étroit
  const depot = !id && mode === 'depot';
  const valide = depot ? !!(f.client_id && f.date_bc && docs.length) : f.client_id && (f.numero.trim() || !senelec) && f.date_bc && lignes.length && lignes.every((l) => l.pba_type && l.quantite !== '' && l.prix_unitaire_ht !== '');

  const save = async () => {
    setBusy(true); setError('');
    try {
      const body = depot ? { ...f, statut: 'OUVERT', depot: true, lignes: [] }
        : { ...f, lignes: lignes.map((l) => ({ ...l, ...(isAdmin ? {} : { reprise_livree: undefined, reprise_facturee: undefined }) })) };
      if (!isAdmin) delete body.reprise_paye_ht;
      const r = id ? await marcheAPI.update(id, body) : await marcheAPI.create(body);
      const mid = id || r.data.id;
      const echecs = [];
      for (const d of docs) {
        // eslint-disable-next-line no-await-in-loop
        try { await marcheAPI.joindre(mid, d.file); } catch (e) { echecs.push(`${d.file.name} : ${apiError(e)}`); }
      }
      const info = `${r.data.message}${docs.length - echecs.length ? ` — ${docs.length - echecs.length} document(s) joint(s)` : ''}`;
      navigate(`/marches/${mid}`, { state: { info: echecs.length ? `${info}. Non joint(s) : ${echecs.join(' ; ')}` : info } });
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Layout title={id ? `Modifier le BC ${f.numero}` : 'Nouveau bon de commande client'} plain>
      {!senelec && f.client_id && !id && <Alert severity="info" sx={{ mb: 2 }}>Saisie simplifiée : client, date, types, quantités et prix suffisent. Le BC sera numéroté par DOCC, imprimable, et les BL de ce client pourront y être rattachés.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {!id && (
        <ToggleButtonGroup exclusive color="primary" value={mode} onChange={(e, v) => v && setMode(v)} sx={{ mb: 2, flexWrap: 'wrap' }} aria-label="Mode de saisie">
          <ToggleButton value="saisie" sx={{ textTransform: 'none', px: 2 }}>Remplir le formulaire</ToggleButton>
          <ToggleButton value="depot" sx={{ textTransform: 'none', px: 2 }}>Déposer le BC sans saisie</ToggleButton>
        </ToggleButtonGroup>
      )}
      {depot && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Déposez le fichier du BC (PDF ou photo) et indiquez le client : le BC est enregistré tout de suite et marqué <b>« à compléter »</b>.
          Les types, quantités et prix pourront être saisis plus tard (bouton <i>Compléter</i> sur sa fiche) pour suivre le reste à livrer et facturer au prix du BC.
        </Alert>
      )}
      <input ref={fileInput} type="file" hidden multiple accept="application/pdf,image/*" data-testid="bc-document" onChange={(e) => { ajouterDocs(e.target.files); e.target.value = ''; }} />
      {!docs.length && (
        <Box onClick={() => fileInput.current?.click()} onDragOver={(e) => { e.preventDefault(); setSurvol(true); }} onDragLeave={() => setSurvol(false)}
          onDrop={(e) => { e.preventDefault(); setSurvol(false); ajouterDocs(e.dataTransfer.files); }} role="button" tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInput.current?.click()}
          sx={{ mb: 2, p: 2, border: '2px dashed', borderColor: survol ? 'primary.main' : tokens.line, borderRadius: 2, bgcolor: survol ? '#EEF3FA' : '#fff',
            display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', flexWrap: 'wrap' }}>
          <UploadFileOutlined sx={{ fontSize: 34, color: tokens.steel }} />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontWeight: 600 }}>{depot ? 'Déposez le BC du client' : 'Joindre le BC reçu du client (facultatif)'}</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>PDF ou photo, 10 Mo maximum — glissez-le ici ou cliquez. Il s'affiche à côté du formulaire pour faciliter la saisie, puis est conservé avec le BC.</Typography>
          </Box>
          <Button variant="outlined" startIcon={<AttachFileOutlined />} onClick={(e) => { e.stopPropagation(); fileInput.current?.click(); }}>Choisir un fichier</Button>
        </Box>
      )}
      <Grid container spacing={2}>
      {docs.length > 0 && (
        <Grid item xs={12} lg={5} xl={5.5}>
          <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
            <Panel title="BC du client" flush sx={{ mb: 2 }}
              action={<Button size="small" startIcon={<AddOutlined />} onClick={() => fileInput.current?.click()} disabled={docs.length >= 10}>Autre fichier</Button>}>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', px: 2, pb: 1 }}>
                {docs.map((d, k) => (
                  <Box key={d.url} sx={{ display: 'flex', alignItems: 'center', border: '1px solid', borderColor: k === vu ? 'primary.main' : tokens.line, borderRadius: 1, pl: 1 }}>
                    <Button size="small" sx={{ textTransform: 'none', maxWidth: 220 }} onClick={() => setVu(k)}><Typography noWrap sx={{ fontSize: 13 }}>{d.file.name}</Typography></Button>
                    <IconButton size="small" aria-label={`Retirer ${d.file.name}`} onClick={() => retirerDoc(k)}><CloseOutlined sx={{ fontSize: 16 }} /></IconButton>
                  </Box>
                ))}
              </Box>
              {docs[vu] && (/^image\//.test(docs[vu].file.type)
                ? <Box component="img" src={docs[vu].url} alt={docs[vu].file.name} sx={{ display: 'block', width: '100%', maxHeight: '75vh', objectFit: 'contain', bgcolor: tokens.concrete }} />
                : <Box component="iframe" title={docs[vu].file.name} src={docs[vu].url} sx={{ display: 'block', width: '100%', height: '75vh', border: 0, bgcolor: tokens.concrete }} />)}
            </Panel>
          </Box>
        </Grid>
      )}
      <Grid item xs={12} lg={docs.length ? 7 : 12} xl={docs.length ? 6.5 : 12}>
      <Panel title="Bon de commande" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={c ? 6 : 4}>
            <TextField select fullWidth size="small" label="Client" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })}>
              {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.nom}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={senelec ? 8 : 12} md={c ? (senelec ? 4 : 6) : (senelec ? 3 : 4.5)}>
            <TextField fullWidth size="small" label={senelec && !depot ? 'N° du bon de commande' : 'N° de commande du client (facultatif)'} placeholder={senelec ? 'ex. 2026114986' : 'ex. BC-0425 — vide : numéro DOCC'}
              value={f.numero} onChange={(e) => setF({ ...f, numero: e.target.value })}
              helperText={(!senelec || depot) && !id ? 'Un numéro DOCC (BCC-AAAA-NNN) est attribué à l’enregistrement' : undefined} />
          </Grid>
          {senelec && <Grid item xs={4} md={c ? 2 : 1.5}><TextField fullWidth size="small" label="BC n°" placeholder="9" value={f.rang} inputProps={{ inputMode: 'numeric' }} onChange={(e) => setF({ ...f, rang: digits(e.target.value) })} /></Grid>}
          <Grid item xs={6} md={c ? 6 : 1.75}><TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={f.date_bc} onChange={(e) => setF({ ...f, date_bc: e.target.value })} /></Grid>
          {!depot && <Grid item xs={6} md={c ? 6 : 1.75}>
            <TextField select fullWidth size="small" label="État" value={f.statut} onChange={(e) => setF({ ...f, statut: e.target.value })}>
              <MenuItem value="OUVERT">En cours</MenuItem><MenuItem value="SOLDE">Soldé</MenuItem><MenuItem value="ANNULE">Annulé</MenuItem>
            </TextField>
          </Grid>}
          <Grid item xs={12} md={c ? 12 : 8}><TextField fullWidth size="small" label="Objet" value={f.objet} onChange={(e) => setF({ ...f, objet: e.target.value })} /></Grid>
          <Grid item xs={12} md={c ? 12 : 4}>
            <TextField fullWidth size="small" label="Délai de paiement (jours)" value={f.delai_paiement_jours} inputProps={{ inputMode: 'numeric' }}
              helperText={senelec ? 'À compter de la validation par la comptabilité du client' : 'À compter de la date de facture'} onChange={(e) => setF({ ...f, delai_paiement_jours: digits(e.target.value) })} />
          </Grid>
        </Grid>
      </Panel>

      {!depot && <Panel title="Quantités et prix" flush sx={{ mb: 2 }}
        action={isAdmin && senelec && (
          <Button size="small" endIcon={<ExpandMore sx={{ transform: historique ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />} onClick={() => setHistorique(!historique)}>
            Historique avant l'ERP
          </Button>
        )}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>Type de support</TableCell><TableCell align="right">Quantité</TableCell><TableCell align="right">Prix unitaire HT</TableCell>
                <TableCell align="right">Montant HT</TableCell>
                {histo && <><TableCell align="right">Livré avant l'ERP</TableCell><TableCell align="right">Facturé avant l'ERP</TableCell></>}
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lignes.map((l, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <TextField select variant="standard" fullWidth value={l.pba_type} SelectProps={{ SelectDisplayProps: { 'aria-label': `Type ${i + 1}` } }}
                      onChange={(e) => { const t = types.find((x) => x.code === e.target.value); setL(i, { pba_type: e.target.value, prix_unitaire_ht: l.prix_unitaire_ht || String(t?.prix_ht || '') }); }}>
                      {types.map((t) => <MenuItem key={t.code} value={t.code} disabled={lignes.some((x, j) => j !== i && x.pba_type === t.code)}>{typeLabel(t.code)}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell align="right"><TextField variant="standard" value={l.quantite} inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' }, 'aria-label': `Quantité ${i + 1}` }} onChange={(e) => setL(i, { quantite: digits(e.target.value) })} /></TableCell>
                  <TableCell align="right"><TextField variant="standard" value={l.prix_unitaire_ht} inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' }, 'aria-label': `Prix ${i + 1}` }} onChange={(e) => setL(i, { prix_unitaire_ht: digits(e.target.value) })} /></TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{fcfa((Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0))}</TableCell>
                  {histo && (
                    <>
                      <TableCell align="right"><TextField variant="standard" value={l.reprise_livree} inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }} onChange={(e) => setL(i, { reprise_livree: digits(e.target.value) })} /></TableCell>
                      <TableCell align="right"><TextField variant="standard" value={l.reprise_facturee} inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }} onChange={(e) => setL(i, { reprise_facturee: digits(e.target.value) })} /></TableCell>
                    </>
                  )}
                  <TableCell><IconButton size="small" disabled={lignes.length === 1} aria-label={`Retirer la ligne ${i + 1}`} onClick={() => setLignes(lignes.filter((_, j) => j !== i))}><DeleteOutline fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell><Button size="small" startIcon={<AddOutlined />} onClick={() => setLignes([...lignes, { pba_type: '', quantite: '', prix_unitaire_ht: '', reprise_livree: '', reprise_facturee: '' }])}>Ajouter un type</Button></TableCell>
                <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>Total HT</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontFamily: tokens.mono }}>{fcfa(total)}</TableCell>
                <TableCell colSpan={histo ? 3 : 1} />
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Collapse in={histo}>
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', bgcolor: '#F7F8FA' }}>
            <TextField size="small" label="Facturé ET payé avant l'ERP (F HT)" value={f.reprise_paye_ht} inputProps={{ inputMode: 'numeric' }} sx={{ minWidth: 280 }}
              onChange={(e) => setF({ ...f, reprise_paye_ht: digits(e.target.value) })} />
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', flex: 1, minWidth: 240 }}>
              Pour un BC commencé avant l'ERP. Les factures antérieures encore impayées se saisissent une par une sur la fiche du BC.
            </Typography>
          </Box>
        </Collapse>
      </Panel>}
      <Panel title="Notes" sx={{ mb: 2 }}>
        <TextField fullWidth multiline minRows={2} size="small" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Conditions, contacts, particularités…" />
      </Panel>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button onClick={() => navigate(-1)}>Annuler</Button>
        <Button variant="contained" disabled={!valide || busy} onClick={save}>{busy ? 'Enregistrement…' : depot ? 'Déposer le BC' : docs.length ? `Enregistrer avec ${docs.length} document(s)` : 'Enregistrer'}</Button>
      </Box>
      </Grid>
      </Grid>
    </Layout>
  );
};

export default MarcheForm;
